import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { NAV_GROUPS, applyRoleFilter, applyPermissionFilter, filterGroups, flattenLeaves } from "@/components/layout/navConfig";

/** Jump-to-a-page search in the top bar (it used to sit at the bottom of the left side bar). */
export function TopSearch() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOrgAdmin, seesWholePortfolio } = useAuth();
  const { canView } = usePermissions();
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  const groups = useMemo(
    () => applyPermissionFilter(applyRoleFilter(NAV_GROUPS, { seesWholePortfolio, isOrgAdmin }), canView),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seesWholePortfolio, isOrgAdmin, canView]
  );
  const q = query.trim().toLowerCase();
  const results = useMemo(() => (q ? flattenLeaves(filterGroups(groups, q, projectId), projectId).slice(0, 8) : []), [q, groups, projectId]);

  useEffect(() => { setQuery(""); }, [location.pathname]);
  useEffect(() => {
    if (!q) return undefined;
    const h = (e) => { if (!boxRef.current?.contains(e.target)) setQuery(""); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [q]);

  const go = (to) => { navigate(to); setQuery(""); };

  return (
    <div ref={boxRef} data-tour="top-search" className="relative hidden w-[240px] shrink-0 md:block lg:w-[300px]">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#8B9BB8]" />
      <input
        data-testid="sidebar-search-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && results[0]) go(results[0].to); if (e.key === "Escape") setQuery(""); }}
        placeholder="Jump to a page…"
        aria-label="Jump to a page"
        className="h-8 w-full rounded-full border border-[#4A5260] bg-[#2E353F] pl-9 pr-8 text-xs text-[#ECF4F7] placeholder:text-[#6B7A8D] focus:border-[#910029] focus:outline-none focus:ring-1 focus:ring-[#910029]"
      />
      {query && <button data-testid="sidebar-search-clear-button" onClick={() => setQuery("")} aria-label="Clear search" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#6B7A8D] hover:text-[#D6657F]"><X className="h-3.5 w-3.5" /></button>}
      {q && (
        <div className="absolute right-0 top-full z-50 mt-1.5 max-h-72 w-[320px] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-xl border border-[#4A5260] bg-[#2E353F] py-1 shadow-2xl" data-testid="sidebar-search-results">
          {results.length === 0 ? <p data-testid="sidebar-search-empty" className="px-4 py-2 text-xs text-[#8B9BB8]">No pages match "{query}"</p> :
            results.map((r) => <button key={r.to} onClick={() => go(r.to)} className="block w-full px-4 py-2 text-left text-[13px] text-[#ECF4F7]/85 hover:bg-[#454D5C]">{r.label}</button>)}
        </div>
      )}
    </div>
  );
}
